import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

export const GEMINI_PROMPT_MAX_LENGTH = 5000;
export const GEMINI_PERIOD_MIN = 1;
export const GEMINI_PERIOD_MAX = 24;
// Valeurs par défaut côté entreprise — jamais null côté back, envoyer null sur
// l'un de ces 3 champs le remet à sa valeur ici.
export const GEMINI_DEFAULT_COMPANY_STAFF_PERIOD_MONTHS = 6;
export const GEMINI_DEFAULT_COMPANY_TEAM_PERIOD_MONTHS = 3;
export const GEMINI_DEFAULT_ANNUAL_REPORT_DATE = '01/01';
// Le rapport d'équipe découpe l'année en blocs de N mois à partir du 01/01 :
// seules les durées qui divisent 12 donnent les mêmes périodes chaque année.
export const GEMINI_TEAM_PERIOD_CHOICES = [1, 2, 3, 4, 6, 12];

const handleError = (error, { rejectWithValue, dispatch }) => {
  if (error.response?.status === 401) dispatch(logout());
  if (!error.response) throw error;
  return rejectWithValue({ statusCode: error.response.status, ...error.response.data });
};

// GET /gemini-config
export const fetchGeminiConfig = createAsyncThunk(
  'geminiConfig/fetchGeminiConfig',
  async (_, thunkApi) => {
    try {
      const response = await api.get('/gemini-config');
      return response.data;
    } catch (error) {
      return handleError(error, thunkApi);
    }
  }
);

// PUT /gemini-config — body déjà construit par l'appelant au format imbriqué du
// contrat (ex. { staff: { prompt: "..." } } ou { company: { defaults: { ... } } }) :
// chaque composant n'envoie que ce qu'il modifie, rien à filtrer ici.
export const updateGeminiConfig = createAsyncThunk(
  'geminiConfig/updateGeminiConfig',
  async (body, thunkApi) => {
    try {
      const response = await api.put('/gemini-config', body);
      return response.data;
    } catch (error) {
      return handleError(error, thunkApi);
    }
  }
);

// GET /gemini-config/companies — uniquement les entreprises ayant au moins une
// valeur spécifique (overrides non entièrement à null).
export const fetchCompanyOverrides = createAsyncThunk(
  'geminiConfig/fetchCompanyOverrides',
  async (_, thunkApi) => {
    try {
      const response = await api.get('/gemini-config/companies');
      return response.data;
    } catch (error) {
      return handleError(error, thunkApi);
    }
  }
);

// PUT /gemini-config/companies/:companyId — body : { staffReportPeriodMonths?,
// teamReportPeriodMonths?, annualReportDate? }, null = revient à la valeur par défaut.
export const setCompanyOverrides = createAsyncThunk(
  'geminiConfig/setCompanyOverrides',
  async ({ companyId, body }, thunkApi) => {
    try {
      const response = await api.put(`/gemini-config/companies/${companyId}`, body);
      return response.data;
    } catch (error) {
      return handleError(error, thunkApi);
    }
  }
);

// DELETE /gemini-config/companies/:companyId — retour aux 3 valeurs par défaut
export const removeCompanyOverrides = createAsyncThunk(
  'geminiConfig/removeCompanyOverrides',
  async (companyId, thunkApi) => {
    try {
      const response = await api.delete(`/gemini-config/companies/${companyId}`);
      return response.data;
    } catch (error) {
      return handleError(error, thunkApi);
    }
  }
);

// PUT /gemini-config/api-key — la clé n'est jamais renvoyée par le back ni gardée ici,
// seule la réponse (apiKeyConfigured/apiKeyLast4/apiKeyUpdatedAt) remplace la config.
export const setGeminiApiKey = createAsyncThunk(
  'geminiConfig/setGeminiApiKey',
  async (apiKey, thunkApi) => {
    try {
      const response = await api.put('/gemini-config/api-key', { apiKey });
      return response.data;
    } catch (error) {
      return handleError(error, thunkApi);
    }
  }
);

// DELETE /gemini-config/api-key
export const removeGeminiApiKey = createAsyncThunk(
  'geminiConfig/removeGeminiApiKey',
  async (_, thunkApi) => {
    try {
      const response = await api.delete('/gemini-config/api-key');
      return response.data;
    } catch (error) {
      return handleError(error, thunkApi);
    }
  }
);

const byCompanyName = (a, b) => (a.companyName || '').localeCompare(b.companyName || '');

const initialState = {
  // { staff: { prompt, reportPeriodMonths }, company: { prompts: {...}, defaults: {...} },
  //   updatedAt, apiKeyConfigured, apiKeyLast4, apiKeyUpdatedAt }
  config: null,
  loading: false,
  saving: false,
  companyOverrides: [], // [{ companyId, companyName, overrides, effective, updatedAt }]
  companyOverridesLoading: false,
};

const geminiConfigSlice = createSlice({
  name: 'geminiConfig',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGeminiConfig.pending, (state) => { state.loading = true; })
      .addCase(fetchGeminiConfig.fulfilled, (state, { payload }) => {
        state.config = payload;
        state.loading = false;
      })
      .addCase(fetchGeminiConfig.rejected, (state) => { state.loading = false; })

      .addCase(updateGeminiConfig.pending, (state) => { state.saving = true; })
      .addCase(updateGeminiConfig.fulfilled, (state, { payload }) => {
        state.config = payload;
        state.saving = false;
      })
      .addCase(updateGeminiConfig.rejected, (state) => { state.saving = false; })

      .addCase(fetchCompanyOverrides.pending, (state) => { state.companyOverridesLoading = true; })
      .addCase(fetchCompanyOverrides.fulfilled, (state, { payload }) => {
        state.companyOverrides = Array.isArray(payload) ? [...payload].sort(byCompanyName) : [];
        state.companyOverridesLoading = false;
      })
      .addCase(fetchCompanyOverrides.rejected, (state) => { state.companyOverridesLoading = false; })

      .addCase(setCompanyOverrides.pending, (state) => { state.saving = true; })
      .addCase(setCompanyOverrides.fulfilled, (state, { payload }) => {
        state.companyOverrides = [
          ...state.companyOverrides.filter((p) => p.companyId !== payload.companyId),
          payload,
        ].sort(byCompanyName);
        state.saving = false;
      })
      .addCase(setCompanyOverrides.rejected, (state) => { state.saving = false; })

      .addCase(removeCompanyOverrides.pending, (state) => { state.saving = true; })
      .addCase(removeCompanyOverrides.fulfilled, (state, { meta }) => {
        // Le back supprime la ligne dès que les 3 champs reviennent par défaut :
        // elle disparaît de la liste (qui ne renvoie que les entreprises avec
        // au moins une valeur spécifique), pas juste mise à jour.
        state.companyOverrides = state.companyOverrides.filter((p) => p.companyId !== meta.arg);
        state.saving = false;
      })
      .addCase(removeCompanyOverrides.rejected, (state) => { state.saving = false; })

      .addCase(setGeminiApiKey.pending, (state) => { state.saving = true; })
      .addCase(setGeminiApiKey.fulfilled, (state, { payload }) => {
        state.config = payload;
        state.saving = false;
      })
      .addCase(setGeminiApiKey.rejected, (state) => { state.saving = false; })

      .addCase(removeGeminiApiKey.pending, (state) => { state.saving = true; })
      .addCase(removeGeminiApiKey.fulfilled, (state, { payload }) => {
        state.config = payload;
        state.saving = false;
      })
      .addCase(removeGeminiApiKey.rejected, (state) => { state.saving = false; });
  },
});

export default geminiConfigSlice.reducer;
